import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Phone, Mail, Edit2, Trash2, User, SortAsc, RotateCcw, DollarSign, HelpCircle, Eye } from 'lucide-react';
import api, { customersApi } from '../services/api';
import { formatPhoneNumber, getInitials } from '../utils/helpers';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import MapLink from '../components/MapLink';

// US States for dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// ============================================
// TOOLTIP COMPONENT
// ============================================
const Tooltip = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };
  
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="ml-1.5 p-1 text-gray-400 hover:text-blue-600 focus:outline-none rounded-full hover:bg-blue-50 active:bg-blue-100"
        aria-label="More information"
      >
        <HelpCircle size={14} />
      </button>
      
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
          onClick={handleClose}
        >
          <div 
            className="bg-gray-900 text-white text-sm rounded-xl shadow-2xl max-w-sm w-full p-4 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <p className="font-semibold text-base mb-2 text-blue-300">{title}</p>
            )}
            <div className="text-gray-200 space-y-2">{children}</div>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const Customers = () => {
  const { user } = useBusinessContext();
  const isOwner = user?.role === 'Owner';
  const location = useLocation();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [viewMode, setViewMode] = useState('cards');

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
    confirmText: 'Confirm',
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'NC',
    zipCode: '',
    notes: '',
    // Billing configuration
    billingType: 'PerJob',
    billingDay: '',
    contractRate: '',
    contractStartDate: '',
    contractEndDate: '',
    contractNotes: '',
  });

  // Handle edit request from CustomerDetail page
  useEffect(() => {
    if (location.state?.editCustomerId && customers.length > 0) {
      const customerToEdit = customers.find(c => c.id === location.state.editCustomerId);
      if (customerToEdit) {
        handleEdit(customerToEdit);
      }
      // Clear the router state so it doesn't re-trigger after every reload
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, customers]);

  useEffect(() => {
    loadCustomers();
  }, [showInactive]);

  useEffect(() => {
    let filtered = customers.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm) ||
      customer.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'recent':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'city':
          return (a.city || 'ZZZ').localeCompare(b.city || 'ZZZ');
        default:
          return 0;
      }
    });

    setFilteredCustomers(filtered);
  }, [searchTerm, customers, sortBy]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersApi.getAll(showInactive);
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load customers. Please try again.',
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCustomer) {
        const updateData = {
          id: editingCustomer.id,
          businessId: editingCustomer.businessId,
          name: formData.name,
          email: formData.email || null,
          phone: formData.phone || null,
          address: formData.address,
          city: formData.city || null,
          state: formData.state || null,
          zipCode: formData.zipCode || null,
          notes: formData.notes || null,
          isActive: editingCustomer.isActive,
          createdAt: editingCustomer.createdAt,
          updatedAt: new Date().toISOString(),
          // Billing configuration
          billingType: formData.billingType || 'PerJob',
          billingDay: formData.billingDay ? parseInt(formData.billingDay) : null,
          contractRate: formData.contractRate ? parseFloat(formData.contractRate) : null,
          contractStartDate: formData.contractStartDate || null,
          contractEndDate: formData.contractEndDate || null,
          contractNotes: formData.contractNotes || null,
        };
        await customersApi.update(editingCustomer.id, updateData);
        if (isOwner && !!formData.doNotRequestReview !== !!editingCustomer.doNotRequestReview) {
          try {
            await api.post(`/customers/${editingCustomer.id}/review-opt-out?optOut=${!!formData.doNotRequestReview}`);
          } catch (optErr) {
            setConfirmModal({
              isOpen: true,
              title: 'Review setting not changed',
              message: optErr.response?.data?.error || 'Could not update the review request setting.',
              variant: 'warning',
              confirmText: 'OK',
              onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            });
          }
        }
        if (isOwner && !!formData.followUpOptOut !== !!editingCustomer.followUpOptOut) {
          try {
            await api.post(`/customers/${editingCustomer.id}/follow-up-opt-out?optOut=${!!formData.followUpOptOut}`);
          } catch (optErr) {
            setConfirmModal({
              isOpen: true,
              title: 'Follow-up setting not changed',
              message: optErr.response?.data?.error || 'Could not update the follow-up setting.',
              variant: 'warning',
              confirmText: 'OK',
              onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            });
          }
        }
      } else {
        const createData = {
          name: formData.name,
          email: formData.email || null,
          phone: formData.phone || null,
          address: formData.address,
          city: formData.city || null,
          state: formData.state || null,
          zipCode: formData.zipCode || null,
          notes: formData.notes || null,
          // Billing configuration
          billingType: formData.billingType || 'PerJob',
          billingDay: formData.billingDay ? parseInt(formData.billingDay) : null,
          contractRate: formData.contractRate ? parseFloat(formData.contractRate) : null,
          contractStartDate: formData.contractStartDate || null,
          contractEndDate: formData.contractEndDate || null,
          contractNotes: formData.contractNotes || null,
        };
        await customersApi.create(createData);
      }
      
      await loadCustomers();
      handleCloseModal();
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error saving customer:', error);
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save customer. Please try again.',
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
    }
  };

  const handleSendPortalAccess = async (customer) => {
    if (!customer.email) {
      setConfirmModal({
        isOpen: true,
        title: 'No Email Address',
        message: 'This customer does not have an email address. Please add one before sending portal access.',
        variant: 'warning',
        onConfirm: () => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          handleEdit(customer);
        },
        confirmText: 'Add Email'
      });
      return;
    }
    
    setConfirmModal({
      isOpen: true,
      title: 'Send Portal Access',
      message: `Send a secure portal access link to ${customer.name} at ${customer.email}?`,
      variant: 'info',
      onConfirm: async () => {
        try {
          await customersApi.sendPortalAccess(customer.id);
          
          setConfirmModal({
            isOpen: true,
            title: 'Email Sent!',
            message: `Portal access email sent to ${customer.email}. The link will expire in 1 hour.`,
            variant: 'success',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
          });
        } catch (error) {
          console.error('Error sending portal access:', error);
          
          setConfirmModal({
            isOpen: true,
            title: 'Error Sending Email',
            message: 'Failed to send portal access email. Please check the customer\'s email address and try again.',
            variant: 'danger',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
          });
        }
      },
      confirmText: 'Send Email',
      cancelText: 'Cancel'
    });
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || 'NC',
      zipCode: customer.zipCode || '',
      notes: customer.notes || '',
      // Billing configuration
      billingType: customer.billingType || 'PerJob',
      billingDay: customer.billingDay?.toString() || '',
      contractRate: customer.contractRate?.toString() || '',
      contractStartDate: customer.contractStartDate?.split('T')[0] || '',
      contractEndDate: customer.contractEndDate?.split('T')[0] || '',
      contractNotes: customer.contractNotes || '',
      followUpOptOut: !!customer.followUpOptOut,
      doNotRequestReview: !!customer.doNotRequestReview,
    });
    setShowModal(true);
  };

  // OWNER ONLY: Delete/Deactivate customer
  const handleDelete = (customer) => {
    if (!isOwner) return; // Safety check

    const hasHistory = (customer.jobCount || 0) > 0 || (customer.invoiceCount || 0) > 0;
    
    if (hasHistory) {
      setConfirmModal({
        isOpen: true,
        title: 'Deactivate Customer',
        message: `"${customer.name}" has ${customer.jobCount || 0} job(s) and cannot be permanently deleted. Would you like to deactivate them instead? They won't appear in dropdowns but their records will be preserved.`,
        variant: 'warning',
        confirmText: 'Deactivate',
        onConfirm: async () => {
          try {
            await customersApi.deactivate(customer.id);
            await loadCustomers();
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            setSelectedCustomer(null);
          } catch (error) {
            console.error('Error deactivating customer:', error);
          }
        },
      });
    } else {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Customer',
        message: `Are you sure you want to delete "${customer.name}"? This action cannot be undone.`,
        variant: 'danger',
        confirmText: 'Delete',
        onConfirm: async () => {
          try {
            await customersApi.delete(customer.id);
            await loadCustomers();
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            setSelectedCustomer(null);
          } catch (error) {
            console.error('Error deleting customer:', error);
          }
        },
      });
    }
  };

  // OWNER ONLY: Reactivate customer
  const handleReactivate = async (customer) => {
    if (!isOwner) return; // Safety check

    try {
      await customersApi.reactivate(customer.id);
      await loadCustomers();
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Error reactivating customer:', error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'NC',
      zipCode: '',
      notes: '',
      // Billing configuration
      billingType: 'PerJob',
      billingDay: '',
      contractRate: '',
      contractStartDate: '',
      contractEndDate: '',
      contractNotes: '',
    });
  };

  if (loading) return <Loading fullScreen />;

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-600">
            {customers.filter(c => c.isActive !== false).length} active
            {showInactive && customers.filter(c => c.isActive === false).length > 0 && (
              <span className="text-gray-400"> • {customers.filter(c => c.isActive === false).length} inactive</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <Plus size={18} />
          Add Customer
        </button>
      </div>

      {/* Search & Filters */}
      <div className="space-y-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search name, email, phone, address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-9 pr-9 py-2.5 text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
            >
              ×
            </button>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <SortAsc size={16} className="text-gray-400 hidden sm:block" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="name">Name A-Z</option>
              <option value="recent">Recent</option>
              <option value="city">City</option>
            </select>
            
            <div className="hidden sm:flex items-center bg-gray-100 rounded-lg p-1 ml-2">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                }`}
              >
                Cards
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
                }`}
              >
                List
              </button>
            </div>
          </div>
          
          {/* OWNER ONLY: Show inactive toggle */}
          {isOwner && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="hidden sm:inline">Show inactive</span>
              <span className="sm:hidden">Inactive</span>
            </label>
          )}
        </div>

        {searchTerm && (
          <p className="text-sm text-gray-500">
            Found {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Customers Display */}
      {filteredCustomers.length === 0 ? (
        <div className="card text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium mb-2">
            {searchTerm ? 'No customers found' : 'No customers yet'}
          </p>
          <p className="text-gray-400 text-sm mb-4">
            {searchTerm ? 'Try a different search' : 'Add your first customer to get started'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Add Customer
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Card View */}
          {(viewMode === 'cards' || isMobile) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredCustomers.map((customer) => (
                <div 
                  key={customer.id} 
                  className={`card p-0 overflow-hidden transition-all ${
                    customer.isActive === false 
                      ? 'opacity-60 bg-gray-50' 
                      : 'hover:shadow-md'
                  }`}
                >
                  <Link 
                    to={`/customers/${customer.id}`}
                    className="block p-4 pb-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                        customer.isActive === false 
                          ? 'bg-gray-200 text-gray-500' 
                          : 'bg-primary-100 text-primary-700'
                      }`}>
                        {getInitials(customer.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 truncate">{customer.name}</h3>
                          {customer.isActive === false && (
                            <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                              Inactive
                            </span>
                          )}

                          {customer.billingType && customer.billingType !== 'PerJob' && (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded flex items-center gap-1">
                              <DollarSign size={10} />
                              {customer.billingType}
                            </span>
                          )}
                        </div>
                        {customer.city && (
                          <p className="text-sm text-gray-500">{customer.city}, {customer.state}</p>
                        )}
                      </div>
                    </div>
                  </Link>

                  <div className="px-4 pb-3 space-y-1.5">
                    {customer.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail size={14} className="flex-shrink-0" />
                        <a 
                          href={`mailto:${customer.email}`} 
                          className="truncate hover:text-primary-600 transition-colors"
                        >
                          {customer.email}
                        </a>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone size={14} className="flex-shrink-0" />
                        <a 
                          href={`tel:${customer.phone}`} 
                          className="hover:text-primary-600 transition-colors"
                        >
                          {formatPhoneNumber(customer.phone)}
                        </a>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex items-start gap-2 text-sm">
                        <MapLink 
                          address={customer.address}
                          city={customer.city}
                          state={customer.state}
                          zipCode={customer.zipCode}
                          truncate
                          className="line-clamp-1"
                        />
                      </div>
                    )}
                  </div>

                  {((customer.jobCount || 0) > 0 || (customer.invoiceCount || 0) > 0) && (
                    <div className="px-4 pb-3">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {(customer.jobCount || 0) > 0 && (
                          <span>{customer.jobCount} job{customer.jobCount !== 1 ? 's' : ''}</span>
                        )}
                        {(customer.invoiceCount || 0) > 0 && (
                          <span>{customer.invoiceCount} invoice{customer.invoiceCount !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons - Always visible */}
                  <div className="border-t border-gray-100 p-2 bg-gray-50">
                    {customer.isActive === false ? (
                      // OWNER ONLY: Reactivate button
                      isOwner && (
                        <button
                          onClick={() => handleReactivate(customer)}
                          className="w-full px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-green-200"
                        >
                          <RotateCcw size={14} />
                          <span>Reactivate</span>
                        </button>
                      )
                    ) : (
                      <div className="flex items-center gap-2">
                        {customer.email && (
                          <button
                            onClick={() => handleSendPortalAccess(customer)}
                            className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-blue-200"
                            title="Send portal access"
                          >
                            <Mail size={14} />
                            <span className="hidden sm:inline">Portal</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(customer)}
                          className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-gray-200"
                        >
                          <Edit2 size={14} />
                          <span>Edit</span>
                        </button>
                        {/* OWNER ONLY: Delete button */}
                        {isOwner && (
                          <button
                            onClick={() => handleDelete(customer)}
                            className="flex-1 sm:flex-none px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 border border-red-200"
                          >
                            <Trash2 size={14} />
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* List View - Desktop only */}
          {viewMode === 'list' && !isMobile && (
            <div className="card p-0 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-sm font-medium text-gray-600 px-4 py-3">Customer</th>
                    <th className="text-left text-sm font-medium text-gray-600 px-4 py-3">Contact</th>
                    <th className="text-left text-sm font-medium text-gray-600 px-4 py-3">Location</th>
                    <th className="text-center text-sm font-medium text-gray-600 px-4 py-3">Jobs</th>
                    <th className="text-right text-sm font-medium text-gray-600 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCustomers.map((customer) => (
                    <tr 
                      key={customer.id} 
                      className={`hover:bg-gray-50 transition-colors ${
                        customer.isActive === false ? 'opacity-60 bg-gray-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <Link to={`/customers/${customer.id}`} className="flex items-center gap-3 hover:opacity-80">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                            customer.isActive === false 
                              ? 'bg-gray-200 text-gray-500' 
                              : 'bg-primary-100 text-primary-700'
                          }`}>
                            {getInitials(customer.name)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{customer.name}</p>
                              {customer.isActive === false && (
                                <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-xs rounded">
                                  Inactive
                                </span>
                              )}

                              {customer.billingType && customer.billingType !== 'PerJob' && (
                                <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded flex items-center gap-1">
                                  <DollarSign size={10} />
                                  {customer.billingType}
                                </span>
                              )}
                            </div>
                            {customer.notes && (
                              <p className="text-xs text-gray-400 truncate max-w-[200px]">{customer.notes}</p>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {customer.email && (
                            <a href={`mailto:${customer.email}`} className="block text-sm text-gray-600 hover:text-primary-600">
                              {customer.email}
                            </a>
                          )}
                          {customer.phone && (
                            <a href={`tel:${customer.phone}`} className="block text-sm text-gray-600 hover:text-primary-600">
                              {formatPhoneNumber(customer.phone)}
                            </a>
                          )}
                          {!customer.email && !customer.phone && (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {customer.address || customer.city ? (
                          <MapLink 
                            address={customer.address}
                            city={customer.city}
                            state={customer.state}
                            zipCode={customer.zipCode}
                            className="text-sm"
                          >
                            {customer.city && customer.state 
                              ? `${customer.city}, ${customer.state}` 
                              : customer.address 
                                ? customer.address.length > 25 
                                  ? customer.address.substring(0, 25) + '...'
                                  : customer.address
                                : null
                            }
                          </MapLink>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm text-gray-600">{customer.jobCount || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {customer.isActive === false ? (
                            // OWNER ONLY: Reactivate
                            isOwner && (
                              <button
                                onClick={() => handleReactivate(customer)}
                                className="px-2 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1"
                              >
                                <RotateCcw size={14} />
                                <span>Reactivate</span>
                              </button>
                            )
                          ) : (
                            <>
                              <button
                                onClick={() => handleSendPortalAccess(customer)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1.5"
                                title="Send portal access"
                              >
                                <Mail size={16} />
                              </button>
                              <button
                                onClick={() => handleEdit(customer)}
                                className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                              {/* OWNER ONLY: Delete */}
                              {isOwner && (
                                <button
                                  onClick={() => handleDelete(customer)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="John Smith"
              maxLength="200"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input"
                placeholder="john@example.com"
                maxLength="100"
              />
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input"
                placeholder="(555) 555-5555"
                maxLength="20"
              />
            </div>
          </div>

          <div>
            <label className="label">Address *</label>
            <input
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input"
              placeholder="123 Main Street"
              maxLength="500"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input"
                placeholder="Mooresville"
                maxLength="100"
              />
            </div>
            <div>
              <label className="label">State</label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="input"
              >
                <option value="">--</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Zip</label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                className="input"
                placeholder="28117"
                maxLength="10"
              />
            </div>
          </div>

          {/* Billing Configuration - Owner Only */}
          {isOwner && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign size={16} className="text-green-600" />
                <label className="font-medium text-gray-900 flex items-center">
                  Billing Configuration
                  <Tooltip title="Contract & Recurring Billing">
                    <p><strong>Per Job (Default):</strong> Each completed job is invoiced individually. Jobs appear in "Ready to Invoice" after completion.</p>
                    <p className="mt-2"><strong>Weekly / Monthly / Quarterly:</strong> Jobs are collected and invoiced together on the billing day.</p>
                    <p className="mt-2"><strong>Billing Day:</strong> The day of the period when you can generate the consolidated invoice for the <em>previous</em> period's work.</p>
                    <p className="mt-2"><strong>Contract Rate:</strong> Optional fixed amount per period. Leave blank to sum all completed job amounts.</p>
                    <p className="mt-2 text-green-300">💡 Great for lawn care, pool service, cleaning, or any recurring maintenance contracts!</p>
                  </Tooltip>
                </label>
              </div>
              
              <div className="space-y-3">
                {/* Billing Type */}
                <div>
                  <label className="label">Billing Type</label>
                  <select
                    value={formData.billingType}
                    onChange={(e) => setFormData({ ...formData, billingType: e.target.value })}
                    className="input"
                  >
                    <option value="PerJob">Per Job (Default)</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                  </select>
                </div>

                {/* Contract fields - only show when not PerJob */}
                {formData.billingType !== 'PerJob' && (
                  <>
                    {/* Billing Day & Contract Rate - stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Billing Day */}
                      <div>
                        <label className="label">
                          Billing Day
                          <span className="font-normal text-gray-500 ml-1">
                            ({formData.billingType === 'Weekly' ? '1-7' : '1-31'})
                          </span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={formData.billingType === 'Weekly' ? 7 : 31}
                          value={formData.billingDay}
                          onChange={(e) => setFormData({ ...formData, billingDay: e.target.value })}
                          className="input"
                          placeholder={formData.billingType === 'Weekly' ? '1 = Sunday' : '1'}
                        />
                      </div>

                      {/* Contract Rate */}
                      <div>
                        <label className="label">
                          Contract Rate
                          <span className="font-normal text-gray-500 ml-1">(optional)</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.contractRate}
                            onChange={(e) => setFormData({ ...formData, contractRate: e.target.value })}
                            className="input pl-7"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contract Date Range - stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="label">Contract Start</label>
                        <input
                          type="date"
                          value={formData.contractStartDate}
                          onChange={(e) => setFormData({ ...formData, contractStartDate: e.target.value })}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Contract End</label>
                        <input
                          type="date"
                          value={formData.contractEndDate}
                          onChange={(e) => setFormData({ ...formData, contractEndDate: e.target.value })}
                          className="input"
                        />
                      </div>
                    </div>

                    {/* Contract Notes */}
                    <div>
                      <label className="label">Contract Notes</label>
                      <textarea
                        value={formData.contractNotes}
                        onChange={(e) => setFormData({ ...formData, contractNotes: e.target.value })}
                        className="input"
                        rows="2"
                        placeholder="Contract terms, special arrangements..."
                        maxLength="1000"
                      />
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        💡 On day <strong>{formData.billingDay || '?'}</strong>, generate one invoice for all of last {formData.billingType?.toLowerCase()}'s completed jobs.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input"
              rows="2"
              placeholder="Gate codes, preferences, special instructions..."
              maxLength="2000"
            />
          </div>

          {isOwner && editingCustomer && (
            <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.doNotRequestReview}
                onChange={(e) => setFormData({ ...formData, doNotRequestReview: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">Don't ask for a review</span>
                <span className="block text-xs text-gray-500">Hides the Request review button on this customer's completed jobs. Nothing else is affected.</span>
              </span>
            </label>
          )}

          {isOwner && editingCustomer && (
            <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.followUpOptOut}
                onChange={(e) => setFormData({ ...formData, followUpOptOut: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">Don't include in marketing or follow-up suggestions</span>
                <span className="block text-xs text-gray-500">Hides them from Action Plan and Customer Intel and marks them as opted out of marketing emails. Jobs, invoices and service emails are not affected.</span>
              </span>
            </label>
          )}
    <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn btn-secondary flex-1 justify-center"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary flex-1 justify-center">
              {editingCustomer ? 'Update Customer' : 'Add Customer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
      />
    </div>
  );
};

export default Customers;